import React from "react";
import { motion } from "framer-motion";
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaClock,
  FaShieldAlt,
  FaLeaf,
  FaHome,
  FaRupeeSign,
  FaUniversity,
  FaClinicMedical,
  FaShoppingBag,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTwitter,
} from "react-icons/fa";
import "./JBVision.css";
import heroLogo from "../../Assets/Dashboard/JBvision.png";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const cardHoverVariants = {
  hover: {
    y: -8,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
};

const heroContacts = [
  {
    label: "Sales Desk",
    value: "+91 7204 256 555",
    href: "tel:+917204256555",
    icon: <FaPhoneAlt />,
  },
  {
    label: "WhatsApp",
    value: "+91 9632 704 999",
    href: "https://wa.me/919632704999",
    icon: <FaWhatsapp />,
  },
];

const heroStats = [
  { label: "Plots Delivered", value: "200+" },
  { label: "Acres Under Development", value: "25" },
  { label: "Families Onboarded", value: "320+" },
];

const pillars = [
  {
    title: "House for Everyone",
    copy: "Community-centered planning that serves first-time buyers, growing families, and retirees with inclusive layouts and amenities.",
    icon: <FaHome />,
  },
  {
    title: "Competitive Pricing",
    copy: "Premium villas and plots priced for accessibility without compromising on architecture, infrastructure, or comfort.",
    icon: <FaRupeeSign />,
  },
  {
    title: "Long-Term Investment",
    copy: "Prime locations, curated amenities, and assured appreciation make every JB Vision purchase a future-forward asset.",
    icon: <FaShieldAlt />,
  },
];

const projectSpecs = [
  { label: "Plot sizes", value: "1200 – 2400 sq ft" },
  { label: "Rooms", value: "2 – 5" },
  { label: "Bathrooms", value: "2 – 4" },
  { label: "Parking", value: "Private garage" },
];

const listings = [
  {
    name: "JB Vision — Luxury Villa Plots",
    price: "₹ 3,999 / Sqft",
    location: "Site No.121, Hoskote–Malur Road, Behind Morarji Desai PU College, Jadigenahalli",
  },
  {
    name: "JB VISION Phase-II, Luxury Farm Land Plots",
    price: "₹ 6,999 / Sqft",
    location: "Hoskote-Malur Road, Behind Morarji Desai PU College, Jadigenahalli",
  },
  {
    name: "Sri Sai Brindavan Avenue — Independent Homes",
    price: "₹ 3,999 – 3,599 / Sqft",
    location: "Mandur & Surrounding Neighborhoods",
  },
];

const accessibility = [
  {
    title: "Schools & Colleges",
    icon: <FaUniversity />,
    items: [
      "Polaris International School",
      "United World Academy",
      "Narayana e-Techno, Whitefield",
      "VIBGYOR High, Kadugodi",
      "Pinnacle International School",
    ],
  },
  {
    title: "Healthcare",
    icon: <FaClinicMedical />,
    items: [
      "MVJ Hospital",
      "OVUM Hospitals",
      "Woman & Child Speciality",
      "Siliconcity Hospitals",
      "Sathya Sai Orthopaedic",
    ],
  },
  {
    title: "IT Parks & Retail",
    icon: <FaShoppingBag />,
    items: [
      "Orion Uptown Mall",
      "Phoenix Marketcity",
      "VR Bengaluru",
      "Nexus Shantiniketan",
      "Narsapura Industrial Area",
    ],
  },
];

const socialLinks = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61566905600948", icon: <FaFacebookF /> },
  { label: "Instagram", href: "https://www.instagram.com/jbvisionplots/", icon: <FaInstagram /> },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/jb-vision-luxury-villa-plots/", icon: <FaLinkedinIn /> },
  { label: "X", href: "https://x.com/JBGroupsVision", icon: <FaTwitter /> },
];

const officeDetails = [
  {
    label: "Experience Centre",
    value: "#11/3,First floor, Bhattarahalli village Bidarahalli hobli Bangalore, Karnataka - 560036",
    icon: <FaMapMarkerAlt />,
  },
  {
    label: "Call Us",
    value: "+91-7204256555 • +91-9663332139",
    icon: <FaPhoneAlt />,
  },
  {
    label: "Timings",
    value: "Mon – Sun: 09:00 AM – 06:00 PM • Tuesday: Closed",
    icon: <FaClock />,
  },
];

const JBvision = () => {
  return (
    <div className="jbvision">
      {/* Hero Section */}
      <section className="jbvision-hero">
        <div className="hero-overlay-gradient" />
        <div className="jbvision-container hero-grid">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="hero-copy"
          >
            <motion.img
              src={heroLogo}
              alt="JB Vision logo"
              className="jbvision-hero-logo"
              variants={itemVariants}
            />
            <motion.p variants={itemVariants} className="eyebrow">Child Brand Spotlight</motion.p>
            <motion.h1 variants={itemVariants}>
              JB Vision
              <span>Luxury Plots &amp; Villas in East Bengaluru</span>
            </motion.h1>
            <motion.p variants={itemVariants} className="hero-lead">
              Reimagining gated community living along the Hoskote growth
              corridor with landscaped enclaves, smart infrastructure, and
              inclusive pricing curated for every family journey.
            </motion.p>
            <motion.div variants={itemVariants} className="hero-badges">
              <span>Gated Community</span>
              <span>House for Everyone</span>
              <span>24/7 Security</span>
            </motion.div>
            <motion.div variants={itemVariants} className="hero-contacts">
              {heroContacts.map((contact, index) => (
                <motion.a
                  key={contact.value}
                  href={contact.href}
                  className="hero-chip"
                  target={contact.href.startsWith("http") ? "_blank" : "_self"}
                  rel="noreferrer"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {contact.icon}
                  <div>
                    <small>{contact.label}</small>
                    <strong>{contact.value}</strong>
                  </div>
                </motion.a>
              ))}
            </motion.div>
            <motion.div variants={itemVariants} className="hero-stats">
              {heroStats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="hero-stat"
                  variants={itemVariants}
                >
                  <span>{stat.value}</span>
                  <p>{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="hero-sidecard"
            whileHover="hover"
          >
            <motion.div variants={cardHoverVariants}>
              <motion.p variants={itemVariants} className="eyebrow">Why JB Vision</motion.p>
              <motion.h3 variants={itemVariants}>Trusted real-estate craft since 2007.</motion.h3>
              <motion.p variants={itemVariants}>
                JB Groups fuses premium plotting with lifestyle infrastructure,
                making every square foot deliver comfort, connectivity, and
                capital appreciation.
              </motion.p>
              <motion.ul variants={itemVariants}>
                <li>Curated villa plots with modern civic upgrades</li>
                <li>Inclusive amenities: pool, club, fitness, play zones</li>
                <li>Proximity to Whitefield, ORR, and Narsapura hubs</li>
              </motion.ul>
            </motion.div>
          </motion.div>
        </div>
        
        <motion.div
          className="hero-floating-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          whileHover={{ scale: 1.05 }}
        >
          <span>Now Booking</span>
          <p>Experience-centre walkthroughs available daily (Tue closed)</p>
        </motion.div>
      </section>

      {/* Vision Section */}
      <motion.section 
        className="jbvision-vision"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <div className="jbvision-container vision-grid">
          <motion.div
            variants={itemVariants}
            whileHover="hover"
            className="vision-card"
          >
            <motion.div variants={cardHoverVariants}>
              <h2>Our Vision</h2>
              <p>
                At JB Vision we cultivate thriving neighborhoods where design,
                sustainability, and connection shape everyday living. Every plot
                or villa balances comfort with accessibility so buyers across life
                stages can plant roots with confidence.
              </p>
              <p>
                From first-time homeowners to retirement seekers, our masterplans
                uplift the quality of life with thoughtful layouts, steady
                infrastructure, and lush, well-managed commons.
              </p>
            </motion.div>
          </motion.div>
          
          <motion.div
            variants={itemVariants}
            className="vision-pillars"
          >
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                variants={itemVariants}
                whileHover="hover"
                className="pillar-card"
              >
                <motion.div variants={cardHoverVariants}>
                  <div className="pillar-icon">{pillar.icon}</div>
                  <div>
                    <h3>{pillar.title}</h3>
                    <p>{pillar.copy}</p>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Featured Section */}
      <motion.section 
        className="jbvision-featured"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <div className="jbvision-container">
          <motion.div
            variants={itemVariants}
            className="section-header"
          >
            <p className="eyebrow">Featured Property</p>
            <h2>JB Vision — Gated Community Living</h2>
            <p>
              Secure, landscaped precinct spread across Jadigenahalli with
              state-of-the-art amenities, on-ground supervision, and quick
              access to Bengaluru&apos;s most connected corridors.
            </p>
          </motion.div>

          <div className="featured-grid">
            <motion.div
              variants={itemVariants}
              whileHover="hover"
              className="featured-card"
            >
              <motion.div variants={cardHoverVariants}>
                <h3>Location &amp; Access</h3>
                <p>
                  Site No.121, Hoskote Malur Road, behind Morarji Desai PU College,
                  Jadigenahalli, Bengaluru – 562114.
                </p>
                <div className="feature-list">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <FaMapMarkerAlt /> <span>8 min to Hoskote Toll</span>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <FaLeaf /> <span>Landscaped parks &amp; trails</span>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <FaShieldAlt /> <span>24/7 security &amp; surveillance</span>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              whileHover="hover"
              className="featured-card specs-card"
            >
              <motion.div variants={cardHoverVariants}>
                <h3>Project Details</h3>
                <div className="spec-grid">
                  {projectSpecs.map((spec, index) => (
                    <motion.div
                      key={spec.label}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <span>{spec.label}</span>
                      <strong>{spec.value}</strong>
                    </motion.div>
                  ))}
                </div>
                <motion.a
                  className="primary-link"
                  href="https://jbvgroups.com/contact-us/"
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Quote Now
                </motion.a>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Listings Section */}
      <motion.section 
        className="jbvision-listings"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <div className="jbvision-container">
          <motion.div variants={itemVariants} className="section-header">
            <p className="eyebrow">The Property</p>
            <h2>Featured Listings</h2>
          </motion.div>
          <div className="listing-grid">
            {listings.map((listing, index) => (
              <motion.div
                key={listing.name}
                variants={itemVariants}
                whileHover="hover"
                className="listing-card"
              >
                <motion.div variants={cardHoverVariants}>
                  <h3>{listing.name}</h3>
                  <p className="price">{listing.price}</p>
                  <p className="location">
                    <FaMapMarkerAlt /> {listing.location}
                  </p>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section 
        className="jbvision-benefits"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <div className="jbvision-container">
          <motion.div
            variants={itemVariants}
            className="benefits-copy"
          >
            <p className="eyebrow">Our Benefits</p>
            <h2>Life inside JB Vision</h2>
            <p>
              Residents enjoy a sparkling pool, clubhouse fitness center,
              children&apos;s play courts, and scenic walking trails that wind
              through manicured green pockets. Regular community events foster
              a friendly, close-knit neighborhood vibe.
            </p>
          </motion.div>
          <div className="access-grid">
            {accessibility.map((bucket, index) => (
              <motion.div
                key={bucket.title}
                variants={itemVariants}
                whileHover="hover"
                className="access-card"
              >
                <motion.div variants={cardHoverVariants}>
                  <div className="access-icon">{bucket.icon}</div>
                  <h3>{bucket.title}</h3>
                  <ul>
                    {bucket.items.map((item, itemIndex) => (
                      <motion.li
                        key={item}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: itemIndex * 0.1 }}
                        viewport={{ once: true }}
                      >
                        {item}
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className="jbvision-cta"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <div className="jbvision-container cta-grid">
          <motion.div
            variants={itemVariants}
            whileHover="hover"
            className="cta-card"
          >
            <motion.div variants={cardHoverVariants}>
              <p className="eyebrow">Connect with JB Vision</p>
              <h2>Book a site visit or request the latest availability.</h2>
              <p>
                Share your requirement and the JB Vision desk will coordinate a
                guided walkthrough, pricing details, and documentation support.
              </p>
              <div className="contact-list">
                {officeDetails.map((detail, index) => (
                  <motion.div
                    key={detail.label}
                    className="contact-row"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ x: 5 }}
                  >
                    <span>{detail.icon}</span>
                    <div>
                      <strong>{detail.label}</strong>
                      <p>{detail.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
          
          <motion.div
            variants={itemVariants}
            whileHover="hover"
            className="cta-social"
          >
            <motion.div variants={cardHoverVariants}>
              <h3>Follow us</h3>
              <p>
                Real-time launch updates, development milestones, and community
                stories from the site.
              </p>
              <div className="social-row">
                {socialLinks.map((link, index) => (
                  <motion.a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={link.label}
                    initial={{ opacity: 0, scale: 0 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.2, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {link.icon}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default JBvision;